import React, { useEffect, useMemo, useRef, useState } from "react";

const FUNCTION_BASE_URL =
  "https://us-central1-xeroo-farmacias-moscovich.cloudfunctions.net";

const ENDPOINTS = {
  createMeeting: `${FUNCTION_BASE_URL}/createMeeting`,
  startMeeting: `${FUNCTION_BASE_URL}/startMeeting`,
  realtimeSession: `${FUNCTION_BASE_URL}/createRealtimeTranscriptionSession`,
  appendTranscript: `${FUNCTION_BASE_URL}/appendMeetingTranscript`,
  analyze: `${FUNCTION_BASE_URL}/analyzeMeetingIncremental`,
  completeSuggestion: `${FUNCTION_BASE_URL}/completeMeetingSuggestion`,
  finishMeeting: `${FUNCTION_BASE_URL}/finishMeeting`,
  getMeeting: `${FUNCTION_BASE_URL}/getMeeting`,
};

const TRANSCRIPTION_USD_PER_MINUTE = 0.017;
const AI_INPUT_USD_PER_1M = 5;
const AI_OUTPUT_USD_PER_1M = 30;

export default function MeetingTranscriber() {
  const [meetingId, setMeetingId] = useState("");
  const [status, setStatus] = useState("idle");
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState("");

  const [meetingSeconds, setMeetingSeconds] = useState(0);
  const [aiCostUsd, setAiCostUsd] = useState(0);
  const [importantNotes, setImportantNotes] = useState("");

  const [form, setForm] = useState({
    title: "",
    clientName: "",
    projectName: "",
    clientContext: "",
    projectContext: "",
    expectedGoal: "",
    currentSystems: "",
    possibleIntegrations: "",
    notes: "",
  });

  const [partialText, setPartialText] = useState("");
  const [finalText, setFinalText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [meetingResult, setMeetingResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);

  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const streamRef = useRef(null);

  const pendingFinalBufferRef = useRef("");
  const livePartialRef = useRef("");
  const finalTextRef = useRef("");

  const analyzeIntervalRef = useRef(null);
  const autoFlushIntervalRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  const transcriptionCostUsd = useMemo(() => {
    return (meetingSeconds / 60) * TRANSCRIPTION_USD_PER_MINUTE;
  }, [meetingSeconds]);

  const totalCostUsd = transcriptionCostUsd + aiCostUsd;

  useEffect(() => {
    if (!isConnected) return;

    const timer = setInterval(() => {
      setMeetingSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [isConnected]);

  function updateForm(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function setFinalTextSafe(updater) {
    setFinalText((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      finalTextRef.current = next;
      return next;
    });
  }

  async function postJson(url, body = {}) {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || data.ok === false) {
      throw new Error(JSON.stringify(data, null, 2));
    }

    return data;
  }

  async function createMeetingIfNeeded() {
    if (meetingId) return meetingId;

    setStatus("creating_meeting");

    const data = await postJson(ENDPOINTS.createMeeting, {
      ...form,
      importantNotes,
    });

    setMeetingId(data.meetingId);
    return data.meetingId;
  }

  async function startTranscription() {
    try {
      setError("");
      setMeetingResult(null);

      const id = await createMeetingIfNeeded();

      await postJson(ENDPOINTS.startMeeting, { meetingId: id });

      setStatus("creating_realtime_session");

      const sessionData = await postJson(ENDPOINTS.realtimeSession, {
        language: "es",
      });

      const ephemeralKey =
        sessionData?.client_secret?.value || sessionData?.value;

      if (!ephemeralKey) throw new Error("No se recibió ephemeral key");

      setStatus("requesting_microphone");

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      setStatus("connecting_webrtc");

      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;

      dc.onopen = () => {
        setIsConnected(true);
        setStatus("active");
        startAutoAnalysis(id);
        startAutoFlush(id);
      };

      dc.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);

          if (
            data.type ===
            "conversation.item.input_audio_transcription.delta"
          ) {
            const delta = data.delta || "";
            livePartialRef.current += delta;
            setPartialText(livePartialRef.current);
          }

          if (
            data.type ===
            "conversation.item.input_audio_transcription.completed"
          ) {
            const transcript = String(data.transcript || "").trim();

            if (transcript) {
              setFinalTextSafe((prev) => prev + transcript + "\n");
              pendingFinalBufferRef.current += transcript + "\n";
              scheduleSaveTranscript(id);
            }

            livePartialRef.current = "";
            setPartialText("");
          }

          if (data.type === "error") {
            console.error("Realtime error:", data);
            setError(JSON.stringify(data.error || data, null, 2));
          }
        } catch (err) {
          console.error("DataChannel message error", err);
        }
      };

      dc.onerror = () => setError("Error en canal realtime");

      pc.onconnectionstatechange = () => {
        if (
          pc.connectionState === "failed" ||
          pc.connectionState === "disconnected" ||
          pc.connectionState === "closed"
        ) {
          setIsConnected(false);
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpRes = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${ephemeralKey}`,
          "Content-Type": "application/sdp",
        },
        body: offer.sdp,
      });

      if (!sdpRes.ok) throw new Error(await sdpRes.text());

      const answerSdp = await sdpRes.text();

      await pc.setRemoteDescription({
        type: "answer",
        sdp: answerSdp,
      });
    } catch (err) {
      console.error(err);
      setError(err.message || "Error desconocido");
      setStatus("error");
      stopTranscription(false);
    }
  }

  function scheduleSaveTranscript(id) {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    saveTimeoutRef.current = setTimeout(async () => {
      await flushTranscript(id);
    }, 1500);
  }

  function startAutoFlush(id) {
    if (autoFlushIntervalRef.current) clearInterval(autoFlushIntervalRef.current);

    autoFlushIntervalRef.current = setInterval(() => {
      flushTranscript(id);
    }, 15000);
  }

  async function flushTranscript(id = meetingId) {
    let text = pendingFinalBufferRef.current.trim();

    if (!text && livePartialRef.current.trim()) {
      text = livePartialRef.current.trim();

      livePartialRef.current = "";
      setPartialText("");

      setFinalTextSafe((prev) => {
        if (prev.includes(text)) return prev;
        return prev + text + "\n";
      });
    }

    if (!id || !text) return;

    pendingFinalBufferRef.current = "";

    try {
      await postJson(ENDPOINTS.appendTranscript, {
        meetingId: id,
        text,
        source: "final",
      });
    } catch (err) {
      console.error("Error saving transcript", err);
      pendingFinalBufferRef.current = text + "\n" + pendingFinalBufferRef.current;
      setError("No se pudo guardar una parte de la transcripción.");
    }
  }

  function estimateAiCost({ inputText = "", expectedOutputTokens = 1200 }) {
    const inputTokens = Math.ceil(inputText.length / 4);
    const inputCost = (inputTokens / 1_000_000) * AI_INPUT_USD_PER_1M;
    const outputCost = (expectedOutputTokens / 1_000_000) * AI_OUTPUT_USD_PER_1M;
    return inputCost + outputCost;
  }

  function startAutoAnalysis(id) {
    if (analyzeIntervalRef.current) clearInterval(analyzeIntervalRef.current);

    analyzeIntervalRef.current = setInterval(() => {
      analyzeMeeting(id);
    }, 120000);
  }

  async function analyzeMeeting(id = meetingId) {
    if (!id || isAnalyzing) return;

    try {
      setIsAnalyzing(true);
      await flushTranscript(id);

      const estimatedCost = estimateAiCost({
        inputText: finalTextRef.current + importantNotes,
        expectedOutputTokens: 900,
      });

      await postJson(ENDPOINTS.analyze, {
        meetingId: id,
        importantNotes,
      });

      setAiCostUsd((prev) => prev + estimatedCost);

      await refreshMeeting(id);
    } catch (err) {
      console.error(err);
      setError("No se pudo analizar la reunión con IA.");
    } finally {
      setIsAnalyzing(false);
    }
  }

  async function refreshMeeting(id = meetingId) {
    if (!id) return;

    const url = `${ENDPOINTS.getMeeting}?meetingId=${encodeURIComponent(id)}`;
    const res = await fetch(url);
    const data = await res.json();

    if (!res.ok || data.ok === false) {
      throw new Error(JSON.stringify(data, null, 2));
    }

    setSuggestions(data.suggestions || []);

    if (data.meeting?.fullTranscript && !finalTextRef.current) {
      setFinalTextSafe(data.meeting.fullTranscript);
    }

    if (data.meeting?.summary) {
      setMeetingResult(data.meeting.summary);
    }
  }

  async function toggleSuggestionCompleted(suggestion) {
    try {
      await postJson(ENDPOINTS.completeSuggestion, {
        meetingId,
        suggestionId: suggestion.id,
        completed: !suggestion.completed,
      });

      await refreshMeeting(meetingId);
    } catch (err) {
      console.error(err);
      setError("No se pudo actualizar la sugerencia.");
    }
  }

  async function stopTranscription(markStatus = true) {
    try {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      if (analyzeIntervalRef.current) {
        clearInterval(analyzeIntervalRef.current);
        analyzeIntervalRef.current = null;
      }

      if (autoFlushIntervalRef.current) {
        clearInterval(autoFlushIntervalRef.current);
        autoFlushIntervalRef.current = null;
      }

      dcRef.current?.close();
      pcRef.current?.close();

      streamRef.current?.getTracks().forEach((track) => track.stop());

      await flushTranscript(meetingId);
    } catch (err) {
      console.error(err);
    }

    dcRef.current = null;
    pcRef.current = null;
    streamRef.current = null;

    setIsConnected(false);

    if (markStatus) setStatus("paused");
  }

  async function finishMeeting() {
    try {
      if (!meetingId) return;

      setIsFinishing(true);
      setError("");

      await stopTranscription(false);
      await flushTranscript(meetingId);

      const estimatedCost = estimateAiCost({
        inputText: finalTextRef.current + importantNotes,
        expectedOutputTokens: 3500,
      });

      const data = await postJson(ENDPOINTS.finishMeeting, {
        meetingId,
        importantNotes,
      });

      setAiCostUsd((prev) => prev + estimatedCost);
      setMeetingResult(data.result);
      setStatus("finished");

      await refreshMeeting(meetingId);
    } catch (err) {
      console.error(err);
      setError(err.message || "No se pudo finalizar la reunión");
    } finally {
      setIsFinishing(false);
    }
  }

  function copyText(text) {
    navigator.clipboard.writeText(text || "");
  }

  useEffect(() => {
    return () => {
      try {
        if (analyzeIntervalRef.current) clearInterval(analyzeIntervalRef.current);
        if (autoFlushIntervalRef.current)
          clearInterval(autoFlushIntervalRef.current);
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

        dcRef.current?.close();
        pcRef.current?.close();
        streamRef.current?.getTracks().forEach((track) => track.stop());
      } catch (_) {}
    };
  }, []);

  const canEditContext = !isConnected && status !== "finished";

  return (
    <div style={styles.page}>
      <div style={styles.bgBlobOne} />
      <div style={styles.bgBlobTwo} />

      <div style={styles.container}>
        <div style={styles.hero}>
          <div>
            <div style={styles.kicker}>Xeroo Meeting Intelligence</div>
            <h1 style={styles.title}>Gestión de reuniones con IA</h1>
            <p style={styles.subtitle}>
              Transcripción realtime, sugerencias en vivo, minuta final y prompt
              para crear una app a medida.
            </p>
          </div>

          <div style={styles.heroRight}>
            <div style={isConnected ? styles.livePill : styles.statusPill}>
              <span style={styles.liveDot} />
              {isConnected ? "Grabando" : status}
            </div>
          </div>
        </div>

        <div style={styles.metricsGrid}>
          <MetricCard
            label="Tiempo reunión"
            value={formatDuration(meetingSeconds)}
            sub="Tiempo activo"
          />
          <MetricCard
            label="Transcripción"
            value={`USD ${transcriptionCostUsd.toFixed(4)}`}
            sub="Estimado realtime"
          />
          <MetricCard
            label="Procesamiento IA"
            value={`USD ${aiCostUsd.toFixed(4)}`}
            sub="Estimado análisis"
          />
          <MetricCard
            label="Total estimado"
            value={`USD ${totalCostUsd.toFixed(4)}`}
            sub="API estimada"
            highlight
          />
        </div>

        {error && (
          <div style={styles.errorBox}>
            <strong>Error:</strong>
            <pre style={styles.errorPre}>{error}</pre>
          </div>
        )}

        <div style={styles.grid}>
          <div style={styles.left}>
            <section style={styles.glassCard}>
              <h2 style={styles.cardTitle}>Contexto del cliente y proyecto</h2>

              <div style={styles.twoCols}>
                <Input
                  label="Título de reunión"
                  value={form.title}
                  disabled={!canEditContext}
                  onChange={(v) => updateForm("title", v)}
                  placeholder="Ej: Discovery CRM para vendedores"
                />

                <Input
                  label="Cliente / empresa"
                  value={form.clientName}
                  disabled={!canEditContext}
                  onChange={(v) => updateForm("clientName", v)}
                  placeholder="Ej: Grupo Portland"
                />

                <Input
                  label="Nombre del proyecto"
                  value={form.projectName}
                  disabled={!canEditContext}
                  onChange={(v) => updateForm("projectName", v)}
                  placeholder="Ej: CRM comercial a medida"
                />

                <Input
                  label="Objetivo esperado"
                  value={form.expectedGoal}
                  disabled={!canEditContext}
                  onChange={(v) => updateForm("expectedGoal", v)}
                  placeholder="Ej: Mejorar seguimiento comercial"
                />
              </div>

              <Textarea
                label="Contexto del cliente"
                value={form.clientContext}
                disabled={!canEditContext}
                onChange={(v) => updateForm("clientContext", v)}
                placeholder="Rubro, tamaño, operación, equipos..."
              />

              <Textarea
                label="Contexto del proyecto"
                value={form.projectContext}
                disabled={!canEditContext}
                onChange={(v) => updateForm("projectContext", v)}
                placeholder="Qué se quiere construir, problema actual, procesos..."
              />

              <div style={styles.twoCols}>
                <Textarea
                  label="Sistemas actuales"
                  value={form.currentSystems}
                  disabled={!canEditContext}
                  onChange={(v) => updateForm("currentSystems", v)}
                  placeholder="HubSpot, Excel, ERP, WhatsApp..."
                />

                <Textarea
                  label="Integraciones posibles"
                  value={form.possibleIntegrations}
                  disabled={!canEditContext}
                  onChange={(v) => updateForm("possibleIntegrations", v)}
                  placeholder="WhatsApp, email, calendario, facturación..."
                />
              </div>

              <Textarea
                label="Notas previas"
                value={form.notes}
                disabled={!canEditContext}
                onChange={(v) => updateForm("notes", v)}
                placeholder="Todo dato previo que ayude a la IA..."
              />

              {meetingId && (
                <div style={styles.meetingId}>
                  Meeting ID: <code>{meetingId}</code>
                </div>
              )}
            </section>

            <section style={styles.glassCard}>
              <div style={styles.actions}>
                {!isConnected ? (
                  <button style={styles.primaryButton} onClick={startTranscription}>
                    {status === "paused"
                      ? "Continuar transcripción"
                      : "Iniciar reunión"}
                  </button>
                ) : (
                  <button
                    style={styles.warningButton}
                    onClick={() => stopTranscription(true)}
                  >
                    Pausar transcripción
                  </button>
                )}

                <button
                  style={styles.secondaryButton}
                  disabled={!meetingId || isAnalyzing}
                  onClick={() => analyzeMeeting(meetingId)}
                >
                  {isAnalyzing ? "Analizando..." : "Analizar ahora"}
                </button>

                <button
                  style={styles.dangerButton}
                  disabled={!meetingId || isFinishing}
                  onClick={finishMeeting}
                >
                  {isFinishing ? "Finalizando..." : "Finalizar reunión"}
                </button>
              </div>

              <h2 style={styles.cardTitle}>Transcripción final</h2>
              <div style={styles.transcriptBox}>
                {finalText ||
                  "Cuando empieces a hablar, la transcripción final aparecerá acá..."}
              </div>

              <h2 style={styles.cardTitle}>Transcripción parcial</h2>
              <div style={styles.partialBox}>
                {partialText || "El texto parcial aparece en vivo mientras hablás..."}
              </div>
            </section>
          </div>

          <div style={styles.right}>
            <section style={styles.highlightCard}>
              <h2 style={styles.cardTitle}>Notas importantes</h2>
              <p style={styles.cardHint}>
                Escribí acá lo que querés que la IA tenga especialmente en cuenta.
              </p>
              <textarea
                style={styles.importantNotes}
                value={importantNotes}
                onChange={(e) => setImportantNotes(e.target.value)}
                placeholder="Ej: El cliente busca bajar costos de HubSpot. Priorizar WhatsApp, tareas automáticas, reportes por vendedor y migración simple..."
              />
            </section>

            <section style={styles.glassCard}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>Sugerencias y dudas IA</h2>
                <button
                  style={styles.smallButton}
                  disabled={!meetingId}
                  onClick={() => refreshMeeting(meetingId)}
                >
                  Refrescar
                </button>
              </div>

              {suggestions.length === 0 ? (
                <div style={styles.empty}>
                  Todavía no hay sugerencias. Se generarán durante la reunión.
                </div>
              ) : (
                <div style={styles.suggestionList}>
                  {suggestions.map((s) => (
                    <div
                      key={s.id}
                      style={{
                        ...styles.suggestionCard,
                        opacity: s.completed ? 0.5 : 1,
                      }}
                    >
                      <div style={styles.suggestionTop}>
                        <span style={styles.badge}>{s.type}</span>
                        <span style={styles.priority}>{s.priority}</span>
                      </div>

                      <h3 style={styles.suggestionTitle}>{s.title}</h3>
                      <p style={styles.suggestionDetail}>{s.detail}</p>

                      <button
                        style={
                          s.completed ? styles.smallButton : styles.completeButton
                        }
                        onClick={() => toggleSuggestionCompleted(s)}
                      >
                        {s.completed ? "Marcar pendiente" : "Marcar completada"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section style={styles.glassCard}>
              <h2 style={styles.cardTitle}>Resultado final</h2>

              {!meetingResult ? (
                <div style={styles.empty}>
                  Al finalizar se generará la minuta, próximos pasos y prompt de app.
                </div>
              ) : (
                <div>
                  <ResultBlock
                    title="Resumen ejecutivo"
                    text={meetingResult.executiveSummary}
                    onCopy={() => copyText(meetingResult.executiveSummary)}
                  />

                  <ResultBlock
                    title="Minuta"
                    text={meetingResult.meetingMinutes}
                    onCopy={() => copyText(meetingResult.meetingMinutes)}
                  />

                  <ListBlock title="Próximos pasos" items={meetingResult.nextSteps} />
                  <ListBlock
                    title="Requerimientos funcionales"
                    items={meetingResult.functionalRequirements}
                  />
                  <ListBlock title="Integraciones" items={meetingResult.integrations} />
                  <ListBlock
                    title="Riesgos / dudas pendientes"
                    items={meetingResult.risksAndPendingQuestions}
                  />

                  <ResultBlock
                    title="Prompt para crear app"
                    text={meetingResult.appPrompt}
                    onCopy={() => copyText(meetingResult.appPrompt)}
                  />
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, sub, highlight }) {
  return (
    <div style={highlight ? styles.metricCardHighlight : styles.metricCard}>
      <div style={styles.metricLabel}>{label}</div>
      <div style={styles.metricValue}>{value}</div>
      <div style={styles.metricSub}>{sub}</div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, disabled }) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>{label}</span>
      <input
        style={styles.input}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function Textarea({ label, value, onChange, placeholder, disabled }) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>{label}</span>
      <textarea
        style={styles.textarea}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function ResultBlock({ title, text, onCopy }) {
  return (
    <div style={styles.resultBlock}>
      <div style={styles.resultHeader}>
        <h3 style={styles.resultTitle}>{title}</h3>
        <button style={styles.smallButton} onClick={onCopy}>
          Copiar
        </button>
      </div>
      <div style={styles.resultText}>{text || "-"}</div>
    </div>
  );
}

function ListBlock({ title, items }) {
  return (
    <div style={styles.resultBlock}>
      <h3 style={styles.resultTitle}>{title}</h3>
      {!items || items.length === 0 ? (
        <div style={styles.emptySmall}>Sin datos</div>
      ) : (
        <ul style={styles.ul}>
          {items.map((item, idx) => (
            <li key={idx} style={styles.li}>
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  return [
    h > 0 ? String(h).padStart(2, "0") : null,
    String(m).padStart(2, "0"),
    String(s).padStart(2, "0"),
  ]
    .filter(Boolean)
    .join(":");
}

const styles = {
  page: {
    minHeight: "100vh",
    position: "relative",
    overflow: "hidden",
    padding: 24,
    fontFamily: "Inter, Arial, sans-serif",
    color: "#f8fafc",
    background:
      "radial-gradient(circle at top left, rgba(87,95,213,0.45), transparent 35%), radial-gradient(circle at top right, rgba(20,184,166,0.25), transparent 30%), linear-gradient(135deg, #070816 0%, #111827 45%, #0f172a 100%)",
  },
  bgBlobOne: {
    position: "absolute",
    width: 420,
    height: 420,
    borderRadius: "50%",
    background: "rgba(87,95,213,0.22)",
    filter: "blur(70px)",
    top: -120,
    left: -120,
    pointerEvents: "none",
  },
  bgBlobTwo: {
    position: "absolute",
    width: 360,
    height: 360,
    borderRadius: "50%",
    background: "rgba(20,184,166,0.18)",
    filter: "blur(70px)",
    bottom: -80,
    right: -80,
    pointerEvents: "none",
  },
  container: {
    position: "relative",
    zIndex: 1,
    maxWidth: 1500,
    margin: "0 auto",
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    gap: 20,
    alignItems: "flex-start",
    marginBottom: 20,
  },
  kicker: {
    color: "#a5b4fc",
    fontWeight: 800,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    fontSize: 12,
    marginBottom: 8,
  },
  title: {
    fontSize: 38,
    lineHeight: 1.05,
    margin: 0,
    letterSpacing: -1.2,
  },
  subtitle: {
    color: "#cbd5e1",
    margin: "12px 0 0",
    maxWidth: 760,
    lineHeight: 1.5,
  },
  heroRight: {
    display: "flex",
    justifyContent: "flex-end",
  },
  statusPill: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.14)",
    color: "#e5e7eb",
    borderRadius: 999,
    padding: "10px 14px",
    fontWeight: 800,
    backdropFilter: "blur(14px)",
  },
  livePill: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "rgba(34,197,94,0.16)",
    border: "1px solid rgba(34,197,94,0.32)",
    color: "#bbf7d0",
    borderRadius: 999,
    padding: "10px 14px",
    fontWeight: 800,
    backdropFilter: "blur(14px)",
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#22c55e",
    boxShadow: "0 0 18px #22c55e",
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 14,
    marginBottom: 20,
  },
  metricCard: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.13)",
    borderRadius: 22,
    padding: 18,
    backdropFilter: "blur(18px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  },
  metricCardHighlight: {
    background:
      "linear-gradient(135deg, rgba(87,95,213,0.75), rgba(20,184,166,0.45))",
    border: "1px solid rgba(255,255,255,0.22)",
    borderRadius: 22,
    padding: 18,
    backdropFilter: "blur(18px)",
    boxShadow: "0 20px 60px rgba(87,95,213,0.25)",
  },
  metricLabel: {
    fontSize: 12,
    color: "#cbd5e1",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },
  metricValue: {
    fontSize: 26,
    fontWeight: 900,
    marginTop: 8,
    color: "#fff",
  },
  metricSub: {
    fontSize: 12,
    color: "#94a3b8",
    marginTop: 4,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1.2fr 0.8fr",
    gap: 20,
    alignItems: "start",
  },
  left: { display: "flex", flexDirection: "column", gap: 20 },
  right: { display: "flex", flexDirection: "column", gap: 20 },
  glassCard: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.14)",
    borderRadius: 24,
    padding: 20,
    boxShadow: "0 24px 80px rgba(0,0,0,0.35)",
    backdropFilter: "blur(18px)",
  },
  highlightCard: {
    background:
      "linear-gradient(135deg, rgba(87,95,213,0.24), rgba(255,255,255,0.08))",
    border: "1px solid rgba(165,180,252,0.28)",
    borderRadius: 24,
    padding: 20,
    boxShadow: "0 24px 80px rgba(0,0,0,0.35)",
    backdropFilter: "blur(18px)",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
  },
  cardTitle: {
    fontSize: 18,
    margin: "0 0 14px",
    color: "#fff",
  },
  cardHint: {
    marginTop: -6,
    color: "#cbd5e1",
    fontSize: 13,
    lineHeight: 1.45,
  },
  twoCols: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginBottom: 12,
  },
  label: {
    fontSize: 13,
    fontWeight: 800,
    color: "#dbeafe",
  },
  input: {
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 14,
    padding: "12px 13px",
    fontSize: 14,
    outline: "none",
    background: "rgba(15,23,42,0.52)",
    color: "#fff",
  },
  textarea: {
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 14,
    padding: "12px 13px",
    fontSize: 14,
    outline: "none",
    minHeight: 78,
    resize: "vertical",
    fontFamily: "Arial, sans-serif",
    background: "rgba(15,23,42,0.52)",
    color: "#fff",
  },
  importantNotes: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 18,
    padding: 14,
    fontSize: 14,
    outline: "none",
    minHeight: 150,
    resize: "vertical",
    fontFamily: "Arial, sans-serif",
    background: "rgba(15,23,42,0.55)",
    color: "#fff",
    lineHeight: 1.5,
  },
  meetingId: {
    marginTop: 8,
    fontSize: 13,
    color: "#94a3b8",
  },
  actions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 18,
  },
  primaryButton: {
    background: "linear-gradient(135deg, #575fd5, #7c3aed)",
    color: "#fff",
    border: "none",
    borderRadius: 14,
    padding: "13px 18px",
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 16px 35px rgba(87,95,213,0.35)",
  },
  secondaryButton: {
    background: "rgba(255,255,255,0.08)",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 14,
    padding: "13px 18px",
    fontWeight: 900,
    cursor: "pointer",
  },
  warningButton: {
    background: "linear-gradient(135deg, #f59e0b, #f97316)",
    color: "#fff",
    border: "none",
    borderRadius: 14,
    padding: "13px 18px",
    fontWeight: 900,
    cursor: "pointer",
  },
  dangerButton: {
    background: "linear-gradient(135deg, #dc2626, #be123c)",
    color: "#fff",
    border: "none",
    borderRadius: 14,
    padding: "13px 18px",
    fontWeight: 900,
    cursor: "pointer",
  },
  smallButton: {
    background: "rgba(255,255,255,0.08)",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.16)",
    borderRadius: 12,
    padding: "8px 10px",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 12,
  },
  completeButton: {
    background: "linear-gradient(135deg, #16a34a, #22c55e)",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "8px 10px",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 12,
  },
  transcriptBox: {
    minHeight: 260,
    maxHeight: 420,
    overflow: "auto",
    background: "rgba(15,23,42,0.62)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 18,
    padding: 16,
    whiteSpace: "pre-wrap",
    lineHeight: 1.6,
    color: "#e5e7eb",
    marginBottom: 20,
  },
  partialBox: {
    minHeight: 95,
    background: "rgba(250,204,21,0.12)",
    border: "1px solid rgba(250,204,21,0.25)",
    borderRadius: 18,
    padding: 16,
    whiteSpace: "pre-wrap",
    lineHeight: 1.6,
    color: "#fef3c7",
  },
  errorBox: {
    marginBottom: 20,
    background: "rgba(239,68,68,0.14)",
    color: "#fecaca",
    borderRadius: 18,
    padding: 14,
    border: "1px solid rgba(248,113,113,0.3)",
  },
  errorPre: {
    whiteSpace: "pre-wrap",
    margin: "8px 0 0",
  },
  empty: {
    color: "#cbd5e1",
    background: "rgba(15,23,42,0.45)",
    borderRadius: 16,
    padding: 14,
    fontSize: 14,
    lineHeight: 1.45,
  },
  emptySmall: {
    color: "#cbd5e1",
    fontSize: 13,
  },
  suggestionList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  suggestionCard: {
    border: "1px solid rgba(255,255,255,0.13)",
    borderRadius: 18,
    padding: 14,
    background: "rgba(15,23,42,0.46)",
  },
  suggestionTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 8,
  },
  badge: {
    fontSize: 11,
    background: "rgba(99,102,241,0.2)",
    color: "#c7d2fe",
    borderRadius: 999,
    padding: "4px 8px",
    fontWeight: 800,
  },
  priority: {
    fontSize: 11,
    background: "rgba(250,204,21,0.16)",
    color: "#fde68a",
    borderRadius: 999,
    padding: "4px 8px",
    fontWeight: 800,
  },
  suggestionTitle: {
    margin: "0 0 6px",
    fontSize: 15,
    color: "#fff",
  },
  suggestionDetail: {
    margin: "0 0 12px",
    fontSize: 13,
    color: "#cbd5e1",
    lineHeight: 1.45,
  },
  resultBlock: {
    border: "1px solid rgba(255,255,255,0.13)",
    borderRadius: 18,
    padding: 14,
    marginBottom: 12,
    background: "rgba(15,23,42,0.46)",
  },
  resultHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  resultTitle: {
    margin: "0 0 8px",
    fontSize: 15,
    color: "#fff",
  },
  resultText: {
    whiteSpace: "pre-wrap",
    fontSize: 13,
    lineHeight: 1.55,
    color: "#cbd5e1",
  },
  ul: {
    margin: 0,
    paddingLeft: 18,
  },
  li: {
    marginBottom: 6,
    fontSize: 13,
    lineHeight: 1.45,
    color: "#cbd5e1",
  },
};